"use client";

import * as React from "react";
import { cva, type VariantProps } from "class-variance-authority";

import { cn } from "@/lib/utils";
import { Icon } from "./icon";
import { FIELD_LABEL_CLASS } from "./input";

/**
 * Shared visual primitive for closed-state field triggers (Select, ComboBox).
 *
 * Single source of truth for the canonical closed-state pill so Select and
 * ComboBox render pixel-identical triggers. Radix Select applies the classes
 * to its own Trigger; ComboBox applies them to a `div` inside PopoverTrigger.
 *
 * Canonical spec (Cohort `.field-trigger`):
 *   - h-8, gap-2, rounded-lg, bg-background, 1px border-outline
 *   - hover: border-foreground (project "outline-strong" equivalent)
 *   - focus / open: border-primary + a 2px primary/20 ring (no border-width
 *     growth — that shifts layout; mirrors the Input primitive)
 *   - px-2.5 (10px)
 *   - chevron lucide:chevron-down, rotates on data-state=open
 *   - label: a plain block label ABOVE the trigger (no floating animation) —
 *     see FieldTriggerLabel.
 */

const fieldTriggerVariants = cva(
  cn(
    "flex w-full items-center justify-between gap-2 rounded-lg border border-outline bg-background text-sm text-foreground",
    "placeholder:text-on-surface-variant/60",
    "transition-[border-color,box-shadow,background-color] duration-[var(--duration-short-3)] ease-[var(--ease-emphasized)] motion-reduce:transition-none",
    "hover:border-foreground",
    "focus:outline-none focus-visible:outline-none",
    "focus-visible:border-primary focus-visible:ring-2 focus-visible:ring-primary/20",
    "focus-within:border-primary focus-within:ring-2 focus-within:ring-primary/20",
    "data-[state=open]:border-primary data-[state=open]:ring-2 data-[state=open]:ring-primary/20",
    "disabled:bg-muted disabled:text-foreground/60 disabled:border-outline-variant disabled:cursor-not-allowed disabled:hover:border-outline-variant",
    "aria-invalid:border-error aria-invalid:hover:border-error",
    "data-[placeholder]:text-on-surface-variant/60",
  ),
  {
    variants: {
      variant: {
        default: "",
        primary: "",
        secondary: "",
        info: "",
        success: "",
        warning: "",
        error:
          "border-error hover:border-error focus-visible:border-error focus-within:border-error data-[state=open]:border-error",
      },
      inputSize: {
        default: "h-8 px-2.5",
        sm: "h-8 px-2.5 text-xs",
        lg: "h-12 px-3.5",
      },
    },
    defaultVariants: {
      variant: "default",
      inputSize: "default",
    },
  },
);

type FieldTriggerVariantProps = VariantProps<typeof fieldTriggerVariants>;

/**
 * Returns the canonical closed-state pill classes. Apply directly to the
 * actual trigger element (radix Trigger or a div used by PopoverTrigger).
 */
export function fieldTriggerClasses(
  variants: FieldTriggerVariantProps,
  extra?: string,
) {
  return cn(fieldTriggerVariants(variants), extra);
}

export type FieldTriggerLabelProps = {
  htmlFor?: string;
  /** Tints the label with the error colour. */
  isError?: boolean;
  className?: string;
  children: React.ReactNode;
};

/**
 * Static block label for a FieldTrigger — the Cohort spec form pattern: a
 * plain label rendered ABOVE the trigger (no floating animation). Place it
 * before the trigger inside a `flex flex-col gap-1.5` wrapper.
 */
export function FieldTriggerLabel({
  htmlFor,
  isError,
  className,
  children,
}: FieldTriggerLabelProps) {
  return (
    <label
      htmlFor={htmlFor}
      className={cn(FIELD_LABEL_CLASS, isError === true && "text-error", className)}
    >
      {children}
    </label>
  );
}

export type FieldTriggerChevronProps = {
  /**
   * Optional explicit open state. Radix sets `data-state=open` on the trigger
   * ancestor automatically (the chevron's parent), so the CSS-only path works
   * for both Select and ComboBox without this prop.
   */
  isOpen?: boolean;
  className?: string;
};

/**
 * Canonical trailing chevron for a FieldTrigger.
 */
export function FieldTriggerChevron({
  isOpen,
  className,
}: FieldTriggerChevronProps) {
  return (
    <Icon
      icon="lucide:chevron-down"
      size="xs"
      className={cn(
        "text-muted-foreground transition-transform duration-[var(--duration-short-3)] ease-[var(--ease-emphasized)] motion-reduce:transition-none data-[state=open]:rotate-180",
        isOpen === true && "rotate-180",
        className,
      )}
    />
  );
}

export { fieldTriggerVariants };
